import { useCallback, useEffect, useState } from 'react'
import { RoomEvent, type Room } from 'livekit-client'

/**
 * The last line of defence against a SILENT LESSON.
 *
 * Browsers refuse to play audio until the page has a user gesture behind it.
 * The join tap normally supplies one, and a granted microphone unblocks
 * playback outright in Chrome — which is why this has never been the common
 * case. But neither of those is guaranteed for everyone in the room:
 *
 *  - a hidden staff observer publishes nothing, so no `getUserMedia` ever runs
 *    and no permission carve-out applies;
 *  - a child who joins with the microphone off is in the same position;
 *  - iOS Safari discards the activation across a backgrounding, and a phone
 *    call or app switch mid-lesson is ordinary behaviour for a child;
 *  - a reconnect can attach fresh `<audio>` elements long after the tap.
 *
 * When it does happen the failure is total and completely silent: every remote
 * track is subscribed, the tiles animate, the tutor is speaking, and the child
 * hears nothing. There is no error, and — until this hook — nothing anywhere in
 * the app that could tell them or offer a way out. `Stage.tsx` renders bare
 * `<AudioTrack>` elements rather than LiveKit's `RoomAudioRenderer`, and the
 * unlock affordance is the part that came with the prefab we do not use.
 *
 * livekit-client already tracks this for us: it records blocked playback,
 * exposes `canPlaybackAudio`, and emits `AudioPlaybackStatusChanged`. All that
 * was missing was somebody listening, and a button whose click carries the
 * gesture into `startAudio()`.
 *
 * Built on the Room API rather than components-react's `useAudioPlayback`,
 * which is marked `@alpha` — this is the recovery path for the worst failure
 * the product has, and it should not move underneath us on a minor upgrade.
 */
export function useAudioUnlock(room: Room | undefined): {
  /** True only when the browser is actually refusing to play remote audio. */
  blocked: boolean
  /** Call from a real click/tap — the gesture is what makes it work. */
  unlock: () => void
} {
  const [blocked, setBlocked] = useState(false)

  useEffect(() => {
    if (!room) {
      setBlocked(false)
      return
    }
    const sync = () => setBlocked(!room.canPlaybackAudio)
    // Read once on mount as well as on the event: playback can already be
    // blocked by the time this hook attaches, and the event only fires on a
    // CHANGE — so a room that was born blocked would never announce it.
    sync()
    room.on(RoomEvent.AudioPlaybackStatusChanged, sync)
    return () => {
      room.off(RoomEvent.AudioPlaybackStatusChanged, sync)
    }
  }, [room])

  const unlock = useCallback(() => {
    if (!room) return
    // Fire-and-forget by design: `startAudio` resolves once the elements play,
    // and awaiting it here would only delay hiding a prompt the user has
    // already dealt with. A rejection means still-blocked, and the event will
    // keep the prompt up on its own.
    void room.startAudio().catch(() => undefined)
  }, [room])

  return { blocked, unlock }
}
